import { cn } from '~/lib/utils';
import { AgentAsksDemo } from './demos/AgentAsksDemo';
import { ContextPulledDemo } from './demos/ContextPulledDemo';
import { BrandKitDemo } from './demos/BrandKitDemo';
import { EditAndPresentDemo } from './demos/EditAndPresentDemo';
import styles from './FeaturesV2.module.css';

/**
 * Landing v2 features — Paper "05 What the agent does · dark".
 *
 * Three feature lines and bodies of the light landing's `FeaturesSection`, carried over word for
 * word plus a fourth about brand kits, in the Cursor framing Guri pointed at (2026-09-04): copy on one side at the site's own
 * type and spacing (label, title, the body pushed to the foot of a 580px column), and on the other a
 * raised surface-2 panel with the workspace inside it, offset from the panel's corner so it bleeds
 * off the far edge and the bottom. Rows alternate sides like the current landing.
 *
 * The window is a skeleton for now: the bar, the agent pane, the artifacts list and a white slide
 * built from bars. Every row draws the same one until the real per-feature mocks exist.
 */

interface Feature {
  /** Also the React key, so keep it unique. */
  label: string;
  title: string;
  body: string;
  cta: string;
  side: 'left' | 'right';
}

const FEATURES: readonly Feature[] = [
  {
    label: 'Agent',
    title: 'The agent that asks\nbefore it acts.',
    body: 'Deck opens with questions, not assumptions. It understands what you are explaining and who you are talking to before building anything.',
    cta: 'See it ask',
    side: 'right',
  },
  {
    label: 'Context',
    title: 'Your context, pulled\nfrom everywhere.',
    body: 'Deck connects to the tools you already use, like Figma, Linear, and Notion, and pulls in the context it needs to build accurately.',
    cta: 'See the integrations',
    side: 'left',
  },
  {
    label: 'Present',
    title: 'Edit it, shape it,\npresent it your way.',
    body: 'Deck builds a complete canvas from your context, structured, editable, and ready to present. No more wrangling canvases from scratch.',
    cta: 'See how it edits',
    side: 'right',
  },
  {
    label: 'Brand kit',
    title: 'Your brand,\nalready applied.',
    body: 'Give Deck your website or a few files and it reads the logo, the colours and the type into a kit. Every deck after that starts in your brand, not in a template.',
    cta: 'See brand kits',
    side: 'left',
  },
];

/** The skeleton window: the workspace's shape in bars, nothing named. Also the nav menus' preview. */
export function WorkspaceSkeleton() {
  return (
    <div className={styles.window} aria-hidden>
      <div className={styles.bar}>
        <span className={styles.mark} />
        <span className={cn(styles.b, styles.b15)} style={{ width: 120, height: 10 }} />
        <span className={styles.spacer} />
        <span className={cn(styles.b, styles.b10)} style={{ width: 16, height: 16, borderRadius: 4 }} />
        <span className={cn(styles.b, styles.b10)} style={{ width: 16, height: 16, borderRadius: 4 }} />
        <span className={cn(styles.b, styles.b15)} style={{ width: 24, height: 24, borderRadius: 12 }} />
      </div>
      <div className={styles.panes}>
        <div className={styles.agent}>
          <span className={cn(styles.b, styles.b10, styles.bubble)} style={{ width: 240, height: 52 }} />
          <div className={styles.row}>
            <span className={cn(styles.b, styles.b15)} style={{ width: 14, height: 14, borderRadius: 7 }} />
            <span className={cn(styles.b, styles.b15)} style={{ width: 110, height: 10 }} />
            <span className={styles.spacer} />
            <span className={cn(styles.b, styles.b8)} style={{ width: 120, height: 8 }} />
          </div>
          <div className={styles.row}>
            <span className={cn(styles.b, styles.b15)} style={{ width: 14, height: 14, borderRadius: 7 }} />
            <span className={cn(styles.b, styles.b15)} style={{ width: 90, height: 10 }} />
          </div>
          <div className={styles.row}>
            <span className={cn(styles.chip, styles.chipOn)} style={{ width: 120 }} />
            <span className={styles.chip} style={{ width: 104 }} />
            <span className={styles.chip} style={{ width: 96 }} />
          </div>
          <span className={cn(styles.b, styles.b10, styles.bubble)} style={{ width: 200, height: 36 }} />
          <span className={cn(styles.b, styles.b10)} style={{ width: 330, height: 10 }} />
          <span className={cn(styles.b, styles.b10)} style={{ width: 280, height: 10 }} />
          <div className={styles.card}>
            <span className={styles.plate} />
            <span className={cn(styles.b, styles.b15)} style={{ width: 120, height: 10 }} />
          </div>
          <span className={styles.spacer} />
          <span className={styles.composer} />
        </div>
        <div className={styles.directory}>
          <div className={styles.dirBar}>
            <span className={cn(styles.b, styles.b15)} style={{ width: 70, height: 10 }} />
            <span className={styles.spacer} />
            <span className={cn(styles.b, styles.b10)} style={{ width: 14, height: 14, borderRadius: 4 }} />
          </div>
          <div className={styles.list}>
            {[
              { w: 110, on: true },
              { w: 90 },
              { w: 120 },
              { w: 80 },
            ].map((r, i) => (
              <div key={i} className={cn(styles.item, r.on && styles.itemOn)}>
                <span className={cn(styles.thumb, r.on && styles.thumbOn)} />
                <span className={styles.lines}>
                  <span className={cn(styles.b, r.on ? styles.b25 : styles.b15)} style={{ width: r.w, height: 9 }} />
                  <span className={cn(styles.b, r.on ? styles.b10 : styles.b8)} style={{ width: 70, height: 7 }} />
                </span>
              </div>
            ))}
          </div>
        </div>
        <div className={styles.preview}>
          <div className={styles.dirBar}>
            <span className={cn(styles.b, styles.b10)} style={{ width: 16, height: 16, borderRadius: 4 }} />
            <span className={cn(styles.b, styles.b15)} style={{ width: 90, height: 10 }} />
            <span className={cn(styles.b, styles.b8)} style={{ width: 60, height: 8 }} />
            <span className={styles.spacer} />
            <span className={cn(styles.b, styles.b10)} style={{ width: 16, height: 16, borderRadius: 4 }} />
            <span className={cn(styles.b, styles.b10)} style={{ width: 16, height: 16, borderRadius: 4 }} />
          </div>
          <div className={styles.stage}>
            <div className={styles.slide}>
              <span className={cn(styles.b, styles.d20)} style={{ width: 80, height: 8 }} />
              <span className={cn(styles.b, styles.d60)} style={{ width: 300, height: 18, borderRadius: 6 }} />
              <span className={cn(styles.b, styles.d60)} style={{ width: 240, height: 18, borderRadius: 6 }} />
              <span className={styles.spacer} />
              <span className={cn(styles.b, styles.d20)} style={{ width: 200, height: 8 }} />
              <span className={cn(styles.b, styles.d20)} style={{ width: 160, height: 8 }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export interface FeaturesV2Props {
  id?: string;
  className?: string;
  headline?: string;
}

export function FeaturesV2({ id, className, headline = 'Deck asks, finds, and builds.' }: FeaturesV2Props) {
  return (
    <section id={id} className={cn(styles.features, className)} data-name="FeaturesV2">
      <div className={styles.container}>
        <h2 className={styles.headline}>{headline}</h2>
        {FEATURES.map((feature) => (
          <div key={feature.label} className={cn(styles.feature, feature.side === 'left' && styles.featureFlipped)}>
            <div className={styles.copy}>
              <h3 className={styles.title}>{feature.title}</h3>
              <div className={styles.body}>
                <p className={styles.text}>{feature.body}</p>
                <a className={styles.cta} href="/sign-in">
                  {feature.cta}
                </a>
              </div>
            </div>
            <div className={styles.panel}>
              {/* Each row plays its own beat; the rest still draw the skeleton (Guri, 2026-09-04). */}
              {feature.label === 'Agent' ? (
                <AgentAsksDemo />
              ) : feature.label === 'Context' ? (
                <ContextPulledDemo />
              ) : feature.label === 'Present' ? (
                <EditAndPresentDemo />
              ) : feature.label === 'Brand kit' ? (
                <BrandKitDemo />
              ) : (
                <WorkspaceSkeleton />
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

FeaturesV2.displayName = 'FeaturesV2';
